import React, { useState, useEffect, useRef } from 'react';
import { Mic, Search, MapPin, Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';
import { Location } from '../types';
import { searchPhoton, PhotonResult } from '../services/photonService';

interface PhotonAutocompleteInputProps {
  placeholder: string;
  onPlaceSelect: (location: Location) => void;
  defaultValue?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  className?: string;
  inputClassName?: string;
  allowedDepartments?: string[];
  theme?: 'light' | 'dark';
  name?: string;
  autoComplete?: string;
  disabled?: boolean;
  citiesOnly?: boolean;
}

export const PhotonAutocompleteInput: React.FC<PhotonAutocompleteInputProps> = ({ 
  placeholder, 
  onPlaceSelect, 
  defaultValue,
  onKeyDown,
  className,
  inputClassName,
  allowedDepartments,
  theme = 'dark',
  name,
  autoComplete = 'off',
  disabled = false,
  citiesOnly = false
}) => {
  const [inputValue, setInputValue] = useState(defaultValue || '');
  const [suggestions, setSuggestions] = useState<PhotonResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteService = useRef<any>(null);

  const initAutocompleteService = () => {
    if (!autocompleteService.current && window.google && window.google.maps && window.google.maps.places) {
      console.log("[ADMIN] Initialisation de AutocompleteService (Lazy Loading)");
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
    }
  };

  useEffect(() => {
    if (defaultValue !== undefined) {
      setInputValue(defaultValue);
    }
  }, [defaultValue]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchGoogleFallback = (query: string): Promise<PhotonResult[]> => {
    return new Promise((resolve) => {
      initAutocompleteService();
      if (!autocompleteService.current) {
        resolve([]);
        return;
      }

      autocompleteService.current.getPlacePredictions(
        { input: query, componentRestrictions: { country: 'fr' } },
        (predictions: any[], status: any) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            let results = predictions.map(p => ({
              name: p.structured_formatting.main_text,
              address: p.description,
              location: { lat: 0, lng: 0 }, // We'll get exact coords on selection
              placeId: p.place_id // Store placeId for later geocoding
            }));
            
            // Filtrage basique par département pour Google (via le texte de l'adresse)
            // L'adresse contient souvent le code postal en France (ex: "34000 Montpellier")
            if (allowedDepartments && allowedDepartments.length > 0) {
               results = results.filter(r => {
                 // Cherche un code postal à 5 chiffres dans l'adresse
                 const match = r.address.match(/\b\d{5}\b/);
                 if (match) {
                   const dept = match[0].substring(0, 2);
                   return allowedDepartments.includes(dept);
                 }
                 // Si pas de code postal trouvé, on garde par défaut pour ne pas bloquer
                 return true;
               });
            }

            resolve(results);
          } else {
            resolve([]);
          }
        }
      );
    });
  };

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const value = e.target.value;
    setInputValue(value);

    if (value.length > 2) {
      setIsLoading(true);
      
      try {
        // Race condition: Photon vs Timeout (1.5s)
        const photonPromise = searchPhoton(value, 5, allowedDepartments, citiesOnly);
        const timeoutPromise = new Promise<PhotonResult[]>((_, reject) => 
          setTimeout(() => reject(new Error('Timeout')), 3000)
        );

        let results: PhotonResult[] = [];
        try {
          results = await Promise.race([photonPromise, timeoutPromise]);
        } catch (err) {
          // If Photon fails or times out, fallback to Google
          results = await searchGoogleFallback(value);
        }

        // If Photon returned empty results, also try Google as a fallback
        if (results.length === 0) {
           results = await searchGoogleFallback(value);
        }

        setSuggestions(results);
        setIsOpen(true);
      } catch (error) {
        // Silently ignore autocomplete errors
      } finally {
        setIsLoading(false);
      }
    } else {
      setSuggestions([]);
      setIsOpen(false);
    }
  };

  const handleSelectSuggestion = (suggestion: any) => {
    setInputValue(suggestion.address);
    setIsOpen(false);
    
    // If it's a Google fallback result (has placeId but no coords)
    if (suggestion.placeId && suggestion.location.lat === 0) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ placeId: suggestion.placeId }, (results: any, status: any) => {
        if (status === 'OK' && results && results[0]) {
          onPlaceSelect({
            lat: results[0].geometry.location.lat(),
            lng: results[0].geometry.location.lng(),
            address: suggestion.address
          });
        } else {
          // Fallback if geocoding fails
          onPlaceSelect({
            lat: 0,
            lng: 0,
            address: suggestion.address
          });
        }
      });
    } else {
      // It's a normal Photon result
      onPlaceSelect({
        lat: suggestion.location.lat,
        lng: suggestion.location.lng,
        address: suggestion.address
      });
    }
  };

  const startVoiceInput = () => {
    setError(null);
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setError("La reconnaissance vocale n'est pas supportée.");
      return;
    }
    
    try {
      const recognition = new SpeechRecognition();
      recognition.lang = 'fr-FR';
      recognition.onstart = () => setIsListening(true);
      recognition.onend = () => setIsListening(false);
      recognition.onerror = () => {
        setIsListening(false);
        setError("Erreur de reconnaissance vocale.");
      };
      recognition.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        setInputValue(transcript);
        handleInputChange({ target: { value: transcript } } as any);
      };
      recognition.start();
    } catch (error) {
      setIsListening(false);
    }
  };

  return (
    <div ref={containerRef} className={cn("relative w-full", className)}>
      <div className="relative flex items-center gap-2">
        <div className="relative flex-1">
          <input 
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            disabled={disabled}
            onFocus={() => {
              if (disabled) return;
              initAutocompleteService();
              if (inputValue.length > 2) setIsOpen(true);
            }}
            placeholder={placeholder}
            onKeyDown={onKeyDown}
            name={name}
            autoComplete={autoComplete}
            className={cn(
              "w-full border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all",
              disabled ? "bg-zinc-100 text-zinc-500 cursor-not-allowed border-zinc-200" : (
                theme === 'dark' 
                  ? "bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-500" 
                  : "bg-white border-zinc-200 text-black placeholder:text-zinc-400"
              ),
              inputClassName
            )}
          />
          {isLoading && !disabled && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <Loader2 className="w-4 h-4 text-emerald-500 animate-spin" />
            </div>
          )}
        </div>
        {!disabled && (
          <button 
            type="button"
            onClick={startVoiceInput}
            className={cn(
              "p-3 rounded-xl transition-all shrink-0",
              isListening ? "bg-red-500 text-white animate-pulse" : 
              theme === 'dark' ? "bg-zinc-800 text-zinc-400 hover:bg-zinc-700" : "bg-zinc-100 text-zinc-500 hover:bg-zinc-200"
            )}
          >
            <Mic className="w-5 h-5" />
          </button>
        )}
      </div>

      {isOpen && !disabled && suggestions.length > 0 && (
        <div className={cn(
          "absolute z-[200] w-full mt-2 border rounded-xl shadow-2xl overflow-hidden max-h-60 overflow-y-auto",
          theme === 'dark' ? "bg-zinc-900 border-zinc-700" : "bg-white border-zinc-200"
        )}>
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              onClick={() => handleSelectSuggestion(suggestion)}
              className={cn(
                "w-full px-4 py-3 text-left transition-colors flex items-start gap-3 border-b last:border-0",
                theme === 'dark' ? "hover:bg-zinc-800 border-zinc-800" : "hover:bg-zinc-50 border-zinc-100"
              )}
            >
              <MapPin className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
              <div>
                <div className={cn("text-sm font-bold", theme === 'dark' ? "text-white" : "text-black")}>{suggestion.name}</div>
                <div className={cn("text-xs", theme === 'dark' ? "text-zinc-500" : "text-zinc-600")}>{suggestion.address}</div>
              </div>
            </button>
          ))}
        </div>
      )}
      
      {error && (
        <span className="text-xs text-red-500 px-2 mt-1 block">{error}</span>
      )}
    </div>
  );
};
